/**
 * PairingRulesSection 商品搭配規則 — Feat #2168 (G-2166-K-04, Main #2166).
 *
 * Kit/combo = pairing rules between EXISTING SKUs (canvas 1:2 on 0:1
 * Sept - SO PO): each row pairs THIS parent product with a component product
 * (盒子/包材-style consumable — itself a normal product, Figma 1:6). This is
 * NOT the OEM canvas (16:3396) — no grouping semantics here (G-2166-K-06).
 * SO-time behaviour (mandatory auto-add / optional prompt, Figma 1:102–123)
 * is follow-on Sales scope (G-2166-K-05): deliberately NO SO-prompt UI.
 *
 * Columns: paired product (name + id link), 搭配類型 tag (Mandatory =
 * filled PurpleTag / Optional = outline), quantity rendered as this:paired
 * numbers (Figma 1:16), 售價原則 (O1 OPEN — null renders the OPEN badge; an explicit
 * stored mode renders its label with no marker, enum-only data pending
 * 林芊/Judy, Q-K1/K2/K3), active state.
 *
 * G-2166-K-03 replenishment indicator: a dot + tooltip on a rule row when the
 * component's 可供量 (Tier-1 read-through echo) sits strictly below its
 * Tier-2 `planning.reorderPoint` (再訂購點) — via the #2167 PURE helper
 * `componentReplenishmentAlert` (no math in tsx). Data absent ⇒ NO indicator,
 * never a fabricated signal. Sanctioned vocabulary only (可供量 / 再訂購點 /
 * 目標水位 family — the board's banned wording never ships).
 *
 * Lifecycle is deactivate-only (no DELETE): inactive rules stay VISIBLE with
 * a status chip + `inactive-row` styling (data status display — house rule).
 * Self-contained (PlanningSection precedent): hosts its own PairingRuleModal
 * and mutations; drops into the shell as `<PairingRulesSection product />`.
 */

import { useMemo, useState } from 'react';
import { Button, Dropdown, Table, Tag, Tooltip, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListErrorState } from '@/components/common/TableContainer';
import PurpleTag from '@/components/common/PurpleTag';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { componentReplenishmentAlert } from '@/lib/pairing/pairingPricing';
import {
  PAIRING_PRICING_MODE_LABEL_KEYS,
  PAIRING_TYPE_LABEL_KEYS,
  usePairingComponentAvailability,
  usePairingRuleMutations,
  usePairingRules,
} from '@/hooks/usePairingRules';
import { useProductOptions } from '@/hooks/useProducts';
import type { Product } from '@/types/api/product';
import type { ProductPairingRule } from '@/types/api/pairingRule';
import PairingRuleModal from '../modals/PairingRuleModal';
import type { DetailSectionKey, ProductDetailTabKey } from '../types';
import SectionHeader from './SectionHeader';

/* eslint-disable react-refresh/only-export-components -- mount data + the
   pure row-model builder are exported alongside the section so the vitest
   exercises the EXACT model the table renders (house VendorLinesSection /
   CommercialSection precedent). */

/**
 * The section key — PROMOTED into the registry (`DetailSectionKey` /
 * `DETAIL_SECTION_KEYS` / `TAB_SECTION_GROUPS`), so the shell's mount-splice
 * is gone and `?section=pairingRules` resolves through `resolveDetailTab`
 * like every other section. `satisfies` binds it: drop the key from the union
 * and this line fails type-check. The anchor id is NOT restated here — it is
 * `sectionAnchorId('pairingRules')`, the one anchor mechanism.
 */
export const PAIRING_RULES_SECTION_KEY = 'pairingRules' as const satisfies DetailSectionKey;

/**
 * Mount as DATA (COMMERCIAL_ROW_MOUNTS / PRICING_SECTION_MOUNT precedent):
 * 基本資料, directly after the vendors section — a product-master relation,
 * beside the vendor lines (board order). Now the DECLARED position that the
 * registry order in `TAB_SECTION_GROUPS.basicInfo` must keep matching.
 */
export const PAIRING_RULES_MOUNT = {
  tab: 'basicInfo',
  afterSection: 'vendors',
} as const satisfies { tab: ProductDetailTabKey; afterSection: DetailSectionKey };

// ---------------------------------------------------------------------------
// Pure row model (exported for the vitest — no React, no fetch)
// ---------------------------------------------------------------------------

export interface PairingRuleRowModel {
  rule: ProductPairingRule;
  /** Component displayName from the product-options join; null = not found. */
  componentName: string | null;
  componentSku: string | null;
  /** Quantity rendered this:paired (Figma 1:16 — e.g. "1:6"). */
  ratioLabel: string;
  /**
   * O1 OPEN — true when pricingMode is null (undecided pending 林芊/Judy):
   * the 售價原則 cell renders the OPEN badge instead of a mode label.
   */
  isPricingModeOpen: boolean;
  /** G-2166-K-03 — strictly available < reorderPoint; false when data absent. */
  replenishmentAlert: boolean;
  /** Tier-1 可供量 echo for the component; null until the read-through lands. */
  availableQty: number | null;
  /** Tier-2 registry `planning.reorderPoint` of the component; null when unset. */
  reorderPoint: number | null;
}

/**
 * Joins rules × product master × availability read-through into the row view
 * model. Absent inputs stay absent (nulls) — `componentReplenishmentAlert`
 * (the #2167 pure helper) then returns false, so a missing read never
 * fabricates an indicator.
 */
export function buildPairingRuleRows(
  rules: readonly ProductPairingRule[],
  productsById: ReadonlyMap<string, Product>,
  availableQtyByProductId: ReadonlyMap<string, number> | undefined
): PairingRuleRowModel[] {
  return rules.map((rule) => {
    const component = productsById.get(rule.componentProductId);
    const availableQty = availableQtyByProductId?.get(rule.componentProductId) ?? null;
    const reorderPoint = component?.planning?.reorderPoint ?? null;
    return {
      rule,
      componentName: component?.displayName ?? null,
      componentSku: component?.sku ?? null,
      ratioLabel: `${rule.qtyRatio.parentQty}:${rule.qtyRatio.componentQty}`,
      isPricingModeOpen: rule.pricingMode === null,
      replenishmentAlert: componentReplenishmentAlert(availableQty, reorderPoint),
      availableQty,
      reorderPoint,
    };
  });
}

/* eslint-enable react-refresh/only-export-components */

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

interface PairingRulesSectionProps {
  product: Product;
}

export const PairingRulesSection = ({ product }: PairingRulesSectionProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { handleError, handleSuccess } = useErrorHandler();

  const { rules, error, isLoading, refresh } = usePairingRules(product.id);
  const { setRuleActive } = usePairingRuleMutations(product.id);
  const { products: allProducts } = useProductOptions('all');

  const componentIds = useMemo(() => rules.map((rule) => rule.componentProductId), [rules]);
  const { availabilityByProductId } = usePairingComponentAvailability(componentIds);

  const productsById = useMemo(
    () => new Map(allProducts.map((item) => [item.id, item])),
    [allProducts]
  );
  const availableQtyById = useMemo(() => {
    if (!availabilityByProductId) return undefined;
    return new Map(
      [...availabilityByProductId].map(([id, row]) => [id, row.availableQty] as const)
    );
  }, [availabilityByProductId]);

  const rows = useMemo(
    () => buildPairingRuleRows(rules, productsById, availableQtyById),
    [rules, productsById, availableQtyById]
  );

  const [modalRule, setModalRule] = useState<ProductPairingRule | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const openCreate = () => {
    setModalRule(null);
    setModalOpen(true);
  };
  const openEdit = (rule: ProductPairingRule) => {
    setModalRule(rule);
    setModalOpen(true);
  };

  const handleToggleActive = async (rule: ProductPairingRule) => {
    setTogglingId(rule.id);
    try {
      await setRuleActive(rule.id, !rule.isActive);
      handleSuccess(
        t(
          rule.isActive
            ? 'products:pairing.messages.deactivated'
            : 'products:pairing.messages.reactivated'
        )
      );
      refresh();
    } catch (toggleError) {
      handleError(toggleError);
    } finally {
      setTogglingId(null);
    }
  };

  const columns: TableProps<PairingRuleRowModel>['columns'] = [
    {
      title: t('products:pairing.columns.component'),
      key: 'component',
      render: (_: unknown, row) => {
        const name =
          row.componentName ??
          t('products:pairing.componentFallback', { id: row.rule.componentProductId });
        const alertText = t('products:pairing.replenishmentAlert', {
          available: row.availableQty,
          reorderPoint: row.reorderPoint,
        });
        return (
          <div className="tw-flex tw-items-start tw-gap-2">
            {row.replenishmentAlert && (
              <Tooltip title={alertText}>
                <span
                  role="img"
                  aria-label={alertText}
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    marginTop: 7,
                    borderRadius: '50%',
                    backgroundColor: token.colorWarning,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            <div className="tw-flex tw-flex-col tw-gap-0.5">
              <a
                onClick={() => navigate(`/auth/products/${row.rule.componentProductId}`)}
                style={{
                  color: token.colorPrimary,
                  cursor: 'pointer',
                  fontWeight: token.fontWeightStrong,
                }}
              >
                {name}
              </a>
              <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {row.componentSku ?? row.rule.componentProductId}
              </Typography.Text>
            </div>
          </div>
        );
      },
    },
    {
      title: t('products:pairing.columns.type'),
      key: 'pairingType',
      width: 130,
      render: (_: unknown, row) =>
        row.rule.pairingType === 'mandatory' ? (
          // Mandatory = FILLED purple tag; Optional = outline (transparent bg).
          <PurpleTag>{t(PAIRING_TYPE_LABEL_KEYS.mandatory)}</PurpleTag>
        ) : (
          <Tag
            style={{
              color: token.colorPrimary,
              borderColor: token.colorPrimaryBorder,
              backgroundColor: 'transparent',
              borderRadius: token.borderRadiusSM,
            }}
          >
            {t(PAIRING_TYPE_LABEL_KEYS.optional)}
          </Tag>
        ),
    },
    {
      title: t('products:pairing.columns.ratio'),
      key: 'ratio',
      width: 170,
      render: (_: unknown, row) => row.ratioLabel,
    },
    {
      title: t('products:pairing.columns.pricingMode'),
      key: 'pricingMode',
      width: 240,
      render: (_: unknown, row) =>
        row.isPricingModeOpen || row.rule.pricingMode === null ? (
          // O1 OPEN — no mode chosen (null): the informational OPEN badge
          // (token colours), pending the 林芊/Judy ruling.
          <Tooltip title={t('products:pairing.openBadgeHint')}>
            <Tag
              style={{
                margin: 0,
                color: token.colorTextSecondary,
                backgroundColor: token.colorFillQuaternary,
                borderColor: token.colorBorderSecondary,
              }}
            >
              {t('products:pairing.openBadge')}
            </Tag>
          </Tooltip>
        ) : (
          // Explicit stored mode — the label alone, no default marker.
          <span>{t(PAIRING_PRICING_MODE_LABEL_KEYS[row.rule.pricingMode])}</span>
        ),
    },
    {
      title: t('products:pairing.columns.status'),
      key: 'status',
      width: 110,
      render: (_: unknown, row) =>
        row.rule.isActive ? (
          <Tag color="success">{t('common:status.active')}</Tag>
        ) : (
          <Tag>{t('common:status.inactive')}</Tag>
        ),
    },
    {
      title: t('products:fields.action'),
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, row) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                label: t('common:actions.edit'),
                icon: <EditOutlined />,
                disabled: !product.isActive,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  openEdit(row.rule);
                },
              },
              {
                key: 'toggle-active',
                label: row.rule.isActive
                  ? t('products:pairing.actions.deactivate')
                  : t('products:pairing.actions.reactivate'),
                danger: row.rule.isActive,
                style: !row.rule.isActive ? { color: token.colorSuccess } : undefined,
                disabled: !product.isActive || togglingId === row.rule.id,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  void handleToggleActive(row.rule);
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('products:actionsFor', {
              name: row.componentName ?? row.rule.componentProductId,
            })}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <SectionHeader
        title={t('products:pairing.sectionTitle')}
        subtitle={t('products:pairing.sectionSubtitle')}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!product.isActive}
            onClick={openCreate}
          >
            {t('products:pairing.addRule')}
          </Button>
        }
      />
      {error ? (
        <ListErrorState
          title={t('products:pairing.loadFailed')}
          retryLabel={t('common:errors.boundary.retry')}
          onRetry={refresh}
        />
      ) : (
        <TableContainer>
          <Table<PairingRuleRowModel>
            rowKey={(row) => row.rule.id}
            dataSource={rows}
            columns={columns}
            loading={isLoading}
            pagination={false}
            rowClassName={(row) => (row.rule.isActive ? '' : 'inactive-row')}
            locale={{ emptyText: t('products:pairing.empty') }}
            scroll={{ x: 1030 }}
          />
        </TableContainer>
      )}

      <PairingRuleModal
        open={modalOpen}
        product={product}
        rule={modalRule}
        existingRules={rules}
        onCancel={() => setModalOpen(false)}
        onSaved={refresh}
      />
    </div>
  );
};

export default PairingRulesSection;
